import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import '../styles/check.css';

const Check = () => {
    const { po_number, cart } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const location = useLocation();
    const [responseMessage, setResponseMessage] = useState('');
    const [, setPoData] = useState(null);
    const [userId, setUserId] = useState(null);
    const [reportError, setReportError] = useState(false);
    const [errorType, setErrorType] = useState('');
    const [errorExplanation, setErrorExplanation] = useState('');

    useEffect(() => {
        const fetchPoData = async () => {
            // First try to use order data from navigation state
            if (location.state?.orderData) {
                setPoData(location.state.orderData);
                // Set userId based on cart type and order data
                if (cart.includes('CART')) {
                    setUserId(location.state.orderData.item_responsable?.[0]?.user_id || null);
                } else {
                    setUserId(location.state.orderData.frozen_responsable?.[0]?.user_id || null);
                }
                return;
            }

            // If no order data in state, fetch from get-po endpoint
            if (!token) {
                console.error('No token available');
                return;
            }

            try {
                const response = await fetch(`${API_BASE}/get-po/?po_number=${po_number}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Token ${token}`,
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setPoData(data);
                    // Set userId based on cart type and fetched data
                    if (cart.includes('CART')) {
                        setUserId(data.item_responsable?.[0]?.user_id || null);
                    } else {
                        setUserId(data.frozen_responsable?.[0]?.user_id || null);
                    }
                } else {
                    console.error('Failed to fetch PO data', response.status, response.statusText);
                    setPoData(null);
                    setUserId(null);
                }
            } catch (error) {
                console.error('Error:', error);
                setPoData(null);
                setUserId(null);
            }
        };

        fetchPoData();
    }, [token, po_number, cart, location.state]);

    const handleYesClick = async () => {
        if (reportError) {
            const errorRequestBody = {
                user_id: userId,
                po_number: po_number,
                error_type: errorType,
                error_description: errorExplanation,
            };

            try {
                const errorResponse = await fetch(`${API_BASE}/error-po/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`,
                    },
                    body: JSON.stringify(errorRequestBody),
                });

                if (!errorResponse.ok) {
                    setResponseMessage('Error reporting issue: ' + errorResponse.statusText);
                    return;
                }
            } catch (error) {
                console.error('Error reporting issue:', error);
                setResponseMessage('Fetch error: ' + error.message);
                return;
            }
        }

                // Get start_time from location state, localStorage, or generate a new one as fallback
        const storageKey = `pickingStartTime_${po_number}_${cart}`;
        const startTime = location.state?.pickingStartTime ||
                         localStorage.getItem(storageKey) ||
                         new Date().toISOString();

        const requestBody = {
            po_number: po_number,
            cart_code: cart,
            user_id: userId,
            start_time: startTime
        };

        console.log('Request body for check-po-cart:', requestBody);
        console.log('pickingStartTime from location.state:', location.state?.pickingStartTime);
        console.log('pickingStartTime from localStorage:', localStorage.getItem(storageKey));
        console.log('Full location.state:', location.state);
        console.log('Using start_time:', startTime);

        try {
            const response = await fetch(`${API_BASE}/check-po-cart/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`,
                },
                body: JSON.stringify(requestBody),
            });

            if (response.ok) {
                const data = await response.json();
                setResponseMessage('Check successful: ' + JSON.stringify(data));

                // Clean up localStorage after successful check
                localStorage.removeItem(storageKey);

                navigate('/admin');
            } else {
                // The check can fail because the order could not be sent to
                // Loken, in which case nothing was saved and the operator
                // should retry. Show the backend's Spanish message and stay
                // on this screen.
                let message = response.statusText;
                try {
                    const data = await response.json();
                    message = data.error || message;
                } catch (parseError) {
                    // Response had no JSON body; fall back to the status text.
                }
                setResponseMessage(message);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            setResponseMessage('Fetch error: ' + error.message);
        }
    };

    const handleNoClick = () => {
        navigate(`/admin/${po_number}`);
    };

    const cartLabel = cart.includes('CART') ? 'El abarrote' : 'El Frío';
    const cartNumber = cart.split('-')[1];

    return (
        <div className="check-container">
            <button className="check-back-button" onClick={handleNoClick}>
                &#8592;
            </button>
            <div className="check-content">
                <h1 className="check-title">¿{cartLabel} {cartNumber} está correcto?</h1>

                <div className="error-report-toggle">
                    <label className="error-checkbox-label">
                        <input
                            type="checkbox"
                            checked={reportError}
                            onChange={() => setReportError(!reportError)}
                            className="error-checkbox"
                        />
                        Reportar error
                    </label>
                </div>

                {reportError && (
                    <div className="error-report-section">
                        <div className="error-type-field">
                            <label>Tipo de error:</label>
                            <select
                                value={errorType}
                                onChange={(e) => setErrorType(e.target.value)}
                                className="error-select"
                            >
                                <option value="">Seleccione una opción</option>
                                <option value="quantity">Cantidad</option>
                                <option value="tags">Etiquetas</option>
                                <option value="sku">Producto</option>
                            </select>
                        </div>
                        <div className="error-explanation-field">
                            <label>Explicación del error:</label>
                            <textarea
                                value={errorExplanation}
                                onChange={(e) => setErrorExplanation(e.target.value)}
                                className="error-textarea"
                                placeholder="Describa el error encontrado..."
                            />
                        </div>
                    </div>
                )}

                <div className="check-buttons">
                    <button className="check-yes-button" onClick={handleYesClick}>
                        Sí
                    </button>
                    <button className="check-no-button" onClick={handleNoClick}>
                        No
                    </button>
                </div>
                {responseMessage && <p className="check-response">{responseMessage}</p>}
            </div>
        </div>
    );
};

export default Check;
